// Descriptive EN/FR copy for KPI calculation templates.
// This module only explains the methods implemented by the backend
// (backend/src/lib/kpi-calculation.js). It never computes a score.

import { withPercent } from "../../i18n/number-format";

export const KPI_METHODS = ["HIGHER", "LOWER", "TARGET", "RANGE", "THRESHOLD", "CUSTOM"];

// Parameters used when an administrator creates a template without editing them.
// They match the values the previous screen sent, so the request payload is unchanged.
export const KPI_DEFAULT_PARAMETERS = Object.freeze({
    HIGHER: {},
    LOWER: {},
    TARGET: { tolerance: 50 },
    RANGE: { lower: 90, upper: 110, tolerance: 30 },
    THRESHOLD: { points: [{ x: 50, y: 0 }, { x: 100, y: 100 }, { x: 120, y: 120 }] },
    CUSTOM: { expression: "(Actual / Target) * 100" },
});

const METHOD_COPY = {
    en: {
        HIGHER: { label: "Higher is better", symbol: "↑", use: "Use when more is better — revenue, sales, satisfaction.", formula: "Actual ÷ Target × 100" },
        LOWER: { label: "Lower is better", symbol: "↓", use: "Use when less is better — costs, errors, response time.", formula: "Target ÷ Actual × 100" },
        TARGET: { label: "Target is best", symbol: "◎", use: "Use when hitting the target exactly matters; deviation in either direction lowers the score.", formula: "100 − |Actual ÷ Target × 100 − 100| × 100 ÷ Tolerance" },
        RANGE: { label: "Preferred range", symbol: "↔", use: "Use when any result inside an acceptable band is fully satisfactory.", formula: "100 − max(0, Lower − A, A − Upper) × 100 ÷ Tolerance;  A = Actual ÷ Target × 100" },
        THRESHOLD: { label: "Threshold-based", symbol: "▤", use: "Use for minimum / target / stretch levels; the score is interpolated between points.", formula: "f(A), piecewise-linear through the points;  A = Actual ÷ Target × 100" },
        CUSTOM: { label: "Advanced custom formula", symbol: "ƒ", use: "Write an expression with Actual, Target, numbers and + − × ÷ ( ).", formula: "" },
    },
    fr: {
        HIGHER: { label: "Plus élevé est préférable", symbol: "↑", use: "À utiliser lorsque plus est mieux — revenus, ventes, satisfaction.", formula: "Réel ÷ Cible × 100" },
        LOWER: { label: "Plus bas est préférable", symbol: "↓", use: "À utiliser lorsque moins est mieux — coûts, erreurs, délais de réponse.", formula: "Cible ÷ Réel × 100" },
        TARGET: { label: "Cible optimale", symbol: "◎", use: "À utiliser lorsque l’atteinte exacte de la cible importe; tout écart réduit le résultat.", formula: "100 − |Réel ÷ Cible × 100 − 100| × 100 ÷ Tolérance" },
        RANGE: { label: "Plage souhaitée", symbol: "↔", use: "À utiliser lorsque tout résultat compris dans une plage acceptable est pleinement satisfaisant.", formula: "100 − max(0, Inférieur − A, A − Supérieur) × 100 ÷ Tolérance;  A = Réel ÷ Cible × 100" },
        THRESHOLD: { label: "Par seuils", symbol: "▤", use: "Pour des niveaux minimum / cible / dépassement; le résultat est interpolé entre les seuils.", formula: "f(A), linéaire par segments entre les seuils;  A = Réel ÷ Cible × 100" },
        CUSTOM: { label: "Formule personnalisée avancée", symbol: "ƒ", use: "Rédigez une expression avec Actual, Target, des nombres et + − × ÷ ( ).", formula: "" },
    },
};

const lang = language => (language === "fr" ? "fr" : "en");

export function kpiMethodCopy(method, language = "en") {
    const table = METHOD_COPY[lang(language)];
    return table[method] || { label: method || "—", symbol: "•", use: "", formula: "" };
}

const fmt = (value, language) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "—";
    return new Intl.NumberFormat(lang(language) === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 2 }).format(numeric);
};

// A plain-language explanation of a template's parameters (no score is computed).
export function describeKpiParameters(template, language = "en") {
    const fr = lang(language) === "fr";
    const p = template?.parameters || {};
    switch (template?.method) {
        case "HIGHER":
            return fr ? "Résultat de 100 lorsque le réel atteint la cible; il augmente ou diminue proportionnellement."
                : "Scores 100 when Actual equals Target and moves in proportion above or below it.";
        case "LOWER":
            return fr ? "Résultat de 100 lorsque le réel égale la cible; un réel inférieur à la cible augmente le résultat."
                : "Scores 100 when Actual equals Target; results below the target score higher.";
        case "TARGET":
            return fr ? `Résultat maximal exactement sur la cible; atteint 0 à un écart de ±${fmt(p.tolerance ?? 50, language)}\u00a0% de la cible.`
                : `Full score exactly on target; reaches 0 at a deviation of ±${fmt(p.tolerance ?? 50, language)}% of target.`;
        case "RANGE":
            return fr ? `Résultat de 100 entre ${fmt(p.lower ?? 90, language)}\u00a0% et ${fmt(p.upper ?? 110, language)}\u00a0% de la cible; hors de la plage, le résultat diminue de 100 points par tranche de ${fmt(p.tolerance ?? 30, language)} points de pourcentage.`
                : `Full score between ${fmt(p.lower ?? 90, language)}% and ${fmt(p.upper ?? 110, language)}% of target; outside the band the score drops 100 points per ${fmt(p.tolerance ?? 30, language)} percentage points.`;
        case "THRESHOLD": {
            const points = (Array.isArray(p.points) ? p.points : []).map(point => `${fmt(point.x, language)}${fr ? " %" : "%"} → ${fmt(point.y, language)}`).join(" · ");
            return fr ? `Atteinte (% de la cible) → résultat : ${points || "—"}. Interpolation linéaire entre deux seuils; sous le premier ou au-delà du dernier seuil, la valeur extrême s’applique.`
                : `Achievement (% of target) → score: ${points || "—"}. Linear between points; below the first or above the last point, the end value applies.`;
        }
        case "CUSTOM":
            return fr ? "Le résultat est la valeur de l’expression. Une division par zéro ou une valeur invalide ne produit aucun résultat."
                : "The score is the result of the expression. Division by zero or an invalid result produces no score.";
        default:
            return "";
    }
}

export function kpiParameterChips(template, language = "en") {
    const fr = lang(language) === "fr";
    const p = template?.parameters || {};
    if (template?.method === "TARGET") return [[fr ? "Tolérance" : "Tolerance", `±${withPercent(fmt(p.tolerance ?? 50, language), language)}`]];
    if (template?.method === "RANGE") return [
        [fr ? "Inférieur" : "Lower", withPercent(fmt(p.lower ?? 90, language), language)],
        [fr ? "Supérieur" : "Upper", withPercent(fmt(p.upper ?? 110, language), language)],
        [fr ? "Tolérance" : "Tolerance", `${fmt(p.tolerance ?? 30, language)} pts`],
    ];
    return [];
}

// Localized display name. Standard templates are identified by their stable id;
// custom templates keep the name the administrator entered.
export function kpiTemplateName(template, language = "en") {
    if (!template) return "—";
    if (template.isSystem || String(template.id || "").startsWith("standard:")) return kpiMethodCopy(template.method, language).label;
    return template.name || kpiMethodCopy(template.method, language).label;
}

export function isStandardKpiTemplate(template) {
    return Boolean(template?.isSystem || String(template?.id || "").startsWith("standard:"));
}

const VALIDATION_COPY = {
    en: {
        name: "Enter a template name.",
        expression: "Enter a formula.",
        tolerance: "Tolerance must be a number greater than 0.",
        rangeNumbers: "Lower bound, upper bound and drop-off must all be numbers.",
        rangeOrder: "The lower bound must not be greater than the upper bound.",
        rangeTolerance: "The drop-off must be greater than 0.",
        pointsCount: "Use between 2 and 12 threshold points.",
        pointsNumbers: "Every threshold point needs a numeric achievement and score.",
        pointsScore: "Threshold scores must be between 0 and 200.",
        pointsOrder: "Achievement values must increase from one point to the next.",
    },
    fr: {
        name: "Saisissez un nom de modèle.",
        expression: "Saisissez une formule.",
        tolerance: "La tolérance doit être un nombre supérieur à 0.",
        rangeNumbers: "La borne inférieure, la borne supérieure et la décroissance doivent être des nombres.",
        rangeOrder: "La borne inférieure ne peut pas dépasser la borne supérieure.",
        rangeTolerance: "La décroissance doit être supérieure à 0.",
        pointsCount: "Utilisez entre 2 et 12 seuils.",
        pointsNumbers: "Chaque seuil doit avoir une atteinte et un résultat numériques.",
        pointsScore: "Les résultats des seuils doivent être compris entre 0 et 200.",
        pointsOrder: "Les valeurs d’atteinte doivent augmenter d’un seuil à l’autre.",
    },
};

const isNumber = value => (typeof value === "number" && Number.isFinite(value))
    || (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)));

// Mirrors validateKpiTemplate in backend/src/lib/kpi-calculation.js so problems are explained
// before the request is sent. The backend remains the authority and rejects the same input.
export function validateKpiTemplateForm({ name, method, parameters = {} }, language = "en") {
    const t = VALIDATION_COPY[lang(language)];
    if (!String(name || "").trim()) return t.name;
    if (method === "CUSTOM" && !String(parameters.expression || "").trim()) return t.expression;
    if (method === "TARGET" && (!isNumber(parameters.tolerance) || Number(parameters.tolerance) <= 0)) return t.tolerance;
    if (method === "RANGE") {
        if (![parameters.lower, parameters.upper, parameters.tolerance].every(isNumber)) return t.rangeNumbers;
        if (Number(parameters.lower) > Number(parameters.upper)) return t.rangeOrder;
        if (Number(parameters.tolerance) <= 0) return t.rangeTolerance;
    }
    if (method === "THRESHOLD") {
        const points = Array.isArray(parameters.points) ? parameters.points : [];
        if (points.length < 2 || points.length > 12) return t.pointsCount;
        if (points.some(point => !point || !isNumber(point.x) || !isNumber(point.y))) return t.pointsNumbers;
        if (points.some(point => Number(point.y) < 0 || Number(point.y) > 200)) return t.pointsScore;
        for (let i = 1; i < points.length; i++) if (Number(points[i].x) <= Number(points[i - 1].x)) return t.pointsOrder;
    }
    return null;
}
